import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {inject, observer} from 'mobx-react';
import {FormattedMessage, injectIntl} from 'react-intl';
import {action, observable, observe, runInAction} from 'mobx';
import IoMinus from 'react-icons/lib/io/minus';
import IoPlus from 'react-icons/lib/io/plus';
import Slider from '@material-ui/lab/Slider';
import ImageCompressor from 'image-compressor.js';
import AvatarEditor from 'react-avatar-editor';
import {withStyles} from '@material-ui/core/styles';
import Close from 'react-icons/lib/io/close';
import {getFileUrl} from '../../../utils/commonUtility';
import FileAttach from '../../../components/FileAttach/index';
import {getDefaultAvatar, getName, getUserAvatar, isDefaultAvatar, isTeacher, setUserAvatar} from '../../../utils/userUtility';
import WebCamCapture from '../WebCamCapture';
import {typoDark, white, secondary} from '../../../themes/colors';
import Upload from '../../../assets/images/upload2';
import Photo from '../../../assets/images/photo';
import withWebCam from '../withWebCam';
import CircularLoader from '../../../components/circularLoader/CircularLoader';
import SvgIconButton from '../../../components/SvgIconButton';
import Link from '../../../components/Link';
import BaseButton from '../../../components/button/BaseButton';
import BaseButtonWithIcon from '../../../components/button/BaseButtonWithIcon';
import LessonDefaultImage from '../../../assets/images/bg-lesson.png';
import './style.scss';
import DialogWrapper from '../../../components/dialog/Dialog';
import UserInitials from '../../../components/userInitials/UserInitials';
import {DialogTypes} from '../../../constants/enums';

const iconButtonStyle = {
    width: 17,
    minWidth: 17,
    height: 17,
    minHeight: 17,
    color: secondary
};

const styles = () => ({
    dialog: {
        zIndex: 1400
    },
    paper: {
        boxShadow: 'none',
        width: 'auto',
        maxWidth: 'none',
        display: 'block',
        overflowY: 'auto',
        overflowX: 'hidden',
        borderRadius: 16,
        maxHeight: 'calc(100% - 28px)',
        margin: 0,
        backgroundColor: white
    }
});

@injectIntl
@withWebCam
@inject('appStore', 'dialogStore')
@observer
class UploadImageDialog extends Component {
    @observable file = {};
    @observable avatar = this.props.avatar || getUserAvatar();
    @observable position = {x: 0.5, y: 0.5};
    @observable scale = 1;
    @observable isScaled = false;
    @observable editor = null;
    @observable isVideoEnabled = false;
    @observable isWebCamOff = false;
    @observable isEditImage = false;
    @observable isRemovedImage = false;

    componentDidMount() {
        observe(this, 'scale', ({newValue}) => {
            this.isScaled = newValue > 1;
        });
    }

    @action enableVideo = () => {
        this.isVideoEnabled = true;
    };

    @action disableVideo = () => {
        this.isVideoEnabled = false;
    };

    onSave = () => {
        this.props.onSave(this.file, this.isEditImage || this.isScaled);
    };

    @action onClickSave = () => {
        const isDefaultImage = this.avatar === LessonDefaultImage || isDefaultAvatar(this.avatar);

        if (this.editor && !isDefaultImage) {
            // This returns a HTMLCanvasElement, it can be made into a data URL or a blob,
            // drawn on another canvas, or added to the DOM.
            this.editor.getImageScaledToCanvas().toBlob((blob) => {
                runInAction(() => {
                    this.setFile(blob);
                    this.onSave();
                    this.avatar = null;
                    this.isEditImage = false;
                });
            });
        } else {
            this.handleCloseDialog();
        }
    };

    @action onClickRemove = () => {
        this.avatar = this.props.avatar || getUserAvatar();
        this.isEditImage = false;
        this.isScaled = false;
        this.position = {x: 0.5, y: 0.5};
        this.scale = 1;
    };

    @action setFile = (file) => {
        this.file = file;
    };

    @action handleAvatarChange = async (files) => {
        const imageCompressor = new ImageCompressor();
        this.isRemovedImage = false;
        try {
            const compressedFile = await imageCompressor.compress(files[0], {quality: 0.8, maxWidth: 800, maxHeight: 600});
            const avatar = await getFileUrl(compressedFile);
            runInAction(() => {
                this.setFile(compressedFile);
                this.isEditImage = true;
                this.avatar = avatar;
                this.scale = 1;
            });
        } catch (e) {
            this.onLoadError();
        }
    };

    @action onPositionChange = (position) => {
        this.position = position;
        this.isEditImage = true;
    };
    @action onScaleChange = (event, value) => {
        this.scale = +value;
        this.isEditImage = true;
    };
    @action setEditorRef = (editor) => {
        this.editor = editor;
    };
    @action handleCloseDialog = () => {
        this.props.onRequestClose();
    };
    onSnapImage = async (blob) => {
        await this.handleAvatarChange(blob);
        this.disableVideo();
    };

    takeSnapshot = () => {
        this.props.snapshot(this.onSnapImage);
    };
    onLoadError = () => {
        this.props.appStore.createAlertFromMessageId('error', 'notifications.wrongFile');
    };

    @action handleError = () => {
        this.disableVideo();
        this.isWebCamOff = true;
        this.props.appStore.createAlertFromMessageId('error', 'notifications.camIsTurnOff');
    };

    @action componentWillUpdate(nextProps) {
        if (nextProps.avatar && nextProps.avatar !== this.props.avatar) {
            console.log('nextpropso: ' + nextProps.avatar)
            this.avatar = nextProps.avatar;
        }
    }

    @action onMinusClick = () => {
        this.scale -= 0.01;
        this.isEditImage = true;
    };

    @action onPlusClick = () => {
        this.scale += 0.01;
        this.isEditImage = true;
    };

    @action resetAvatar = () => {
        if (!this.props.resetImage) {
            setUserAvatar(getDefaultAvatar());
        } else {
            this.props.resetImage();
        }

        this.scale = 1;
        this.isRemovedImage = true;
    };

    render() {
        const {open, isLoading, title, classes, videoRef, canvasRef, videoWidth, videoHeight,
            setWebCamToCanvas, isSavingBackground, clearWebCam, stream,
            handleSuccess, intl: {formatMessage: translate}, resetImage, showRemoveLink} = this.props;
        const sliderClasses = {
            root: 'slider-range',
            track: 'slider-track',
            trackAfter: 'slider-trackAfter',
            thumb: 'slider-thumb',
            focused: 'slider-focused',
            disabled: 'slider-disabled',
            activated: 'slider-activated'
        };
        const isDefaultImage = this.avatar === LessonDefaultImage || isDefaultAvatar(this.avatar);
        return (
            <DialogWrapper classes={{root: classes.dialog, paper: classes.paper}} onClose={this.handleCloseDialog} open={open}>
                <div className="change-avatar">
                    <SvgIconButton containerClass="change-avatar-close" rippleColor={white} style={iconButtonStyle} onClick={this.handleCloseDialog}>
                        <Close style={{fill: typoDark, fontSize: 14}}/>
                    </SvgIconButton>
                    <h2 className="change-avatar-heading">
                        <FormattedMessage id={`profile.photo.${title}`} />
                    </h2>
                    {isLoading && <CircularLoader containerStyle={{width: '100%', margin: '25px 0 0'}}/>}
                    <div className="change-avatar-container">
                        <div className="change-avatar-name">
                            <FormattedMessage id="profile.photo.name"/>
                        </div>
                        {this.isVideoEnabled ?
                            <WebCamCapture
                                width={276}
                                height={155}
                                webCamProps={{
                                    videoRef,
                                    canvasRef,
                                    videoWidth,
                                    videoHeight,
                                    setWebCamToCanvas,
                                    clearWebCam,
                                    stream,
                                    handleSuccess,
                                    handleError: this.handleError,
                                }}
                                snapshot={this.takeSnapshot}
                            />
                            :
                            (isDefaultAvatar(this.avatar) && isTeacher() ?
                                <div className="photo-editor avatar-initials default"><UserInitials fullName={getName()}/></div> :
                                    <AvatarEditor ref={this.setEditorRef}
                                                  image={this.avatar}
                                                  className="change-user-avatar"
                                                  width={276}
                                                  height={155}
                                                  border={0}
                                                  scale={this.scale}
                                                  position={this.position}
                                                  onPositionChange={this.onPositionChange}
                            />)}
                        <div className="change-avatar-buttons">
                            <FileAttach
                                disabled={this.isVideoEnabled}
                                fileExtension="image/*"
                                style={{display: 'flex', width: 'auto'}}
                                className="button"
                                onChange={this.handleAvatarChange}
                                onError={this.onLoadError}>
                                <BaseButtonWithIcon
                                    viewBox="0 0 20 20"
                                    className="change-avatar-button"
                                    disabled={this.isVideoEnabled}
                                    icon={Upload}
                                    label={translate({id: 'general.upload'})} />
                            </FileAttach>
                            {this.isVideoEnabled ?
                                <BaseButtonWithIcon
                                    viewBox="0 0 20 20"
                                    className="change-avatar-button danger"
                                    onClick={this.takeSnapshot}
                                    icon={Photo}
                                    label={translate({id: 'general.snapshot'})} />
                                :
                                <BaseButtonWithIcon
                                    viewBox="0 0 20 20"
                                    className="change-avatar-button"
                                    disabled={this.isWebCamOff}
                                    onClick={!this.isWebCamOff && this.enableVideo}
                                    icon={Photo}
                                    label={translate({id: 'general.camera'})} />
                            }
                        </div>

                        <div className="change-avatar-scale">
                            <p className={`change-avatar-text ${this.isEditImage ? '' : 'disabled'}`}>
                                <FormattedMessage id="profile.photo.modalHint" />
                            </p>
                            <div className="change-avatar-scale-box">
                                <SvgIconButton
                                    disabled={isDefaultImage || this.scale <= 1}
                                    containerClass="change-avatar-minus"
                                    rippleColor={white}
                                    style={iconButtonStyle}
                                    onClick={this.onMinusClick}>
                                    <IoMinus style={{fill: 'currentColor', fontSize: 16}}/>
                                </SvgIconButton>
                                <Slider
                                    disabled={isDefaultImage}
                                    step={0.01}
                                    min={1}
                                    max={2}
                                    value={this.scale}
                                    onChange={this.onScaleChange}
                                    classes={sliderClasses} />
                                <SvgIconButton
                                    disabled={isDefaultImage || this.scale >= 2}
                                    containerClass="change-avatar-plus"
                                    rippleColor={white}
                                    style={iconButtonStyle}
                                    onClick={this.onPlusClick}>
                                    <IoPlus style={{fill: 'currentColor', fontSize: 16}}/>
                                </SvgIconButton>
                            </div>
                        </div>
                        <div className="change-avatar-btn-wrap">
                            {(!isDefaultImage && !resetImage) && <div className="remove-link" onClick={this.resetAvatar}>
                                <FormattedMessage id="general.remove"/>
                            </div>}
                            {(resetImage && showRemoveLink) && <div className="remove-link alt" onClick={this.resetAvatar}>
                                <FormattedMessage id="general.remove"/>
                            </div>}
                            <BaseButton
                                disabled={(isDefaultImage || (!this.isEditImage && !this.isScaled) || isSavingBackground) && !this.isRemovedImage}
                                className="change-avatar-btn"
                                label={translate({id: `general.${this.isRemovedImage ? 'done' : 'upload'}`})} type="button" onClick={this.onClickSave} />
                        </div>
                    </div>
                </div>
            </DialogWrapper>
        );
    }
}

UploadImageDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onSave: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    isBackgroundLesson: PropTypes.bool
};

export default withStyles(styles)(UploadImageDialog);
